import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, DateInput, TextInput } from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import type { ImoCrewListPrintDetails } from './crewListPrintMeta';

export type ImoCrewListDetailsModalProps = {
  open: boolean;
  /** Default arrival/departure date (usually list period from). */
  defaultArrivalDate?: string;
  onClose: () => void;
  onConfirm: (details: ImoCrewListPrintDetails) => void;
};

const emptyDetails = (defaultArrivalDate = ''): ImoCrewListPrintDetails => ({
  portName: '',
  arrivalDate: defaultArrivalDate,
  voyageNumber: '',
  lastPort: '',
  isArrival: false,
  isDeparture: false,
});

/** Collect FAL Form 5 voyage fields before opening the IMO crew list preview. */
export const ImoCrewListDetailsModal: React.FC<ImoCrewListDetailsModalProps> = ({
  open,
  defaultArrivalDate = '',
  onClose,
  onConfirm,
}) => {
  const { colors } = useTheme();
  const [draft, setDraft] = useState<ImoCrewListPrintDetails>(() =>
    emptyDetails(defaultArrivalDate),
  );

  useEffect(() => {
    if (open) setDraft(emptyDetails(defaultArrivalDate));
  }, [open, defaultArrivalDate]);

  const setField = <K extends keyof ImoCrewListPrintDetails>(
    key: K,
    value: ImoCrewListPrintDetails[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Modal isOpen={open} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>IMO Crew List details</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <p className={cn('mb-4 text-sm', colors.textMuted)}>
          Fill in the voyage fields for FAL Form 5, then continue to the print preview.
        </p>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="imo-print-port"
              className={cn('mb-1 block text-xs font-medium', colors.textMuted)}
            >
              Port of arrival / departure
            </label>
            <TextInput
              id="imo-print-port"
              name="portName"
              value={draft.portName}
              onChange={(e) => setField('portName', e.target.value)}
              placeholder="Port name"
              enterNav
            />
          </div>
          <div>
            <label
              htmlFor="imo-print-date"
              className={cn('mb-1 block text-xs font-medium', colors.textMuted)}
            >
              Date of arrival / departure
            </label>
            <DateInput
              id="imo-print-date"
              name="arrivalDate"
              value={draft.arrivalDate}
              onChange={(e) => setField('arrivalDate', e.target.value)}
            />
          </div>
          <fieldset>
            <legend className={cn('mb-1.5 text-xs font-medium', colors.textMuted)}>
              Arrival / departure
            </legend>
            <div className="flex flex-wrap gap-4">
              <label className={cn('inline-flex cursor-pointer items-center gap-2 text-sm', colors.text)}>
                <input
                  type="checkbox"
                  checked={draft.isArrival}
                  onChange={(e) => setField('isArrival', e.target.checked)}
                  className="rounded border-gray-300"
                />
                Arrival
              </label>
              <label className={cn('inline-flex cursor-pointer items-center gap-2 text-sm', colors.text)}>
                <input
                  type="checkbox"
                  checked={draft.isDeparture}
                  onChange={(e) => setField('isDeparture', e.target.checked)}
                  className="rounded border-gray-300"
                />
                Departure
              </label>
            </div>
          </fieldset>
          <div>
            <label
              htmlFor="imo-print-voyage"
              className={cn('mb-1 block text-xs font-medium', colors.textMuted)}
            >
              Voyage number
            </label>
            <TextInput
              id="imo-print-voyage"
              name="voyageNumber"
              value={draft.voyageNumber}
              onChange={(e) => setField('voyageNumber', e.target.value)}
              placeholder="Voyage number"
              enterNav
            />
          </div>
          <div>
            <label
              htmlFor="imo-print-last-port"
              className={cn('mb-1 block text-xs font-medium', colors.textMuted)}
            >
              Last port of call
            </label>
            <TextInput
              id="imo-print-last-port"
              name="lastPort"
              value={draft.lastPort}
              onChange={(e) => setField('lastPort', e.target.value)}
              placeholder="Last port of call"
              enterNav
            />
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="accent" onClick={() => onConfirm(draft)}>
          Continue to preview
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ImoCrewListDetailsModal;
